import { useState } from 'react';
import { orders } from '@/mocks/orders';
import { statusLabels } from '@/mocks/orders';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';

const statusDistribution = [
  { name: 'Yeni', value: orders.filter(o => o.status === 'yeni').length, color: '#3B82F6' },
  { name: 'Çizimde', value: orders.filter(o => o.status === 'cizimde' || o.status === 'cizim_onay').length, color: '#A855F7' },
  { name: 'Kesimde', value: orders.filter(o => o.status === 'kesimde').length, color: '#F97316' },
  { name: 'Aydınlatma', value: orders.filter(o => o.status === 'aydinlatma' || o.status === 'test').length, color: '#EAB308' },
  { name: 'Paketleme', value: orders.filter(o => o.status === 'paketleme' || o.status === 'kargoya_hazir').length, color: '#1E3A5F' },
  { name: 'Kargoda', value: orders.filter(o => o.status === 'kargoda').length, color: '#7DD3FC' },
  { name: 'Teslim', value: orders.filter(o => o.status === 'teslim').length, color: '#22C55E' },
  { name: 'Sorunlu/İptal', value: orders.filter(o => o.status === 'sorunlu' || o.status === 'iptal').length, color: '#EF4444' },
];

const dailyOrders = [
  { day: '11 Haz', count: 2 },
  { day: '12 Haz', count: 3 },
  { day: '13 Haz', count: 1 },
  { day: '14 Haz', count: 4 },
  { day: '15 Haz', count: 2 },
  { day: '16 Haz', count: 5 },
  { day: '17 Haz', count: 3 },
];

const collectionData = [
  { day: '11 Haz', amount: 0 },
  { day: '12 Haz', amount: 750 },
  { day: '13 Haz', amount: 0 },
  { day: '14 Haz', amount: 2200 },
  { day: '15 Haz', amount: 999 },
  { day: '16 Haz', amount: 1800 },
  { day: '17 Haz', amount: 999 },
];

const cargoDistribution = [
  { name: 'Bekliyor', value: orders.filter(o => o.cargoStatus === 'bekliyor' || (!o.cargoStatus && o.status === 'kargoya_hazir')).length, color: '#94A3B8' },
  { name: 'Hazır', value: orders.filter(o => o.cargoStatus === 'hazir').length, color: '#1E3A5F' },
  { name: 'Yolda', value: orders.filter(o => o.cargoStatus === 'yolda' || o.cargoStatus === 'dagitimda' || o.cargoStatus === 'verildi').length, color: '#3B82F6' },
  { name: 'Teslim', value: orders.filter(o => o.cargoStatus === 'teslim_edildi').length, color: '#22C55E' },
  { name: 'Sorunlu', value: orders.filter(o => o.cargoStatus === 'teslim_edilemedi' || o.cargoStatus === 'iade_surecinde' || o.cargoStatus === 'iade_geldi').length, color: '#EF4444' },
];

const reportList = [
  { title: 'Günlük Sipariş Raporu', desc: 'Bugün alınan siparişlerin detaylı dökümü', icon: 'ri-file-list-3-line' },
  { title: 'Haftalık Sipariş Raporu', desc: 'Bu hafta alınan tüm siparişler', icon: 'ri-file-chart-line' },
  { title: 'Aylık Ciro Raporu', desc: 'Haziran 2026 gelir-gider analizi', icon: 'ri-money-dollar-circle-line' },
  { title: 'Üretim Aşaması Raporu', desc: 'Aşama bazında iş yoğunluğu', icon: 'ri-tools-line' },
  { title: 'Geciken İşler Raporu', desc: 'Teslim tarihi geçen siparişler', icon: 'ri-alert-line' },
  { title: 'Kapıda Ödeme Raporu', desc: 'Tahsilat bekleyen kapıda ödemeler', icon: 'ri-bank-card-line' },
  { title: 'Kargo Teslimat Raporu', desc: 'Kargo bazında teslimat analizi', icon: 'ri-truck-line' },
  { title: 'İade / Sorunlu Sipariş Raporu', desc: 'İade edilen ve sorunlu siparişler', icon: 'ri-error-warning-line' },
  { title: 'En Çok Satılan Ürünler', desc: 'Ürün bazında satış sıralaması', icon: 'ri-medal-line' },
  { title: 'Sipariş Kaynakları Raporu', desc: 'Hangi kanaldan ne kadar sipariş', icon: 'ri-radar-line' },
];

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg px-3 py-2 shadow-md">
        <p className="text-xs text-stone-500">{label}</p>
        <p className="text-sm font-bold text-stone-800">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

const CurrencyTooltip = ({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg px-3 py-2 shadow-md">
        <p className="text-xs text-stone-500">{label}</p>
        <p className="text-sm font-bold text-stone-800">{payload[0].value.toLocaleString('tr-TR')} ₺</p>
      </div>
    );
  }
  return null;
};

export default function ReportsPage() {
  const [selectedPeriod, setSelectedPeriod] = useState('week');

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Raporlar</h2>
          <p className="text-sm text-stone-500 mt-0.5">İş zekası ve performans analizleri</p>
        </div>
        <div className="flex items-center gap-2 bg-stone-100 rounded-lg p-1">
          {[
            { key: 'day', label: 'Günlük' },
            { key: 'week', label: 'Haftalık' },
            { key: 'month', label: 'Aylık' },
          ].map((p) => (
            <button
              key={p.key}
              onClick={() => setSelectedPeriod(p.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                selectedPeriod === p.key ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Daily Orders */}
        <div className="bg-white rounded-xl border border-stone-200/80 p-5">
          <h4 className="text-sm font-semibold text-stone-800 mb-4">Günlük Sipariş Sayısı</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyOrders}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#D4A843" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-xl border border-stone-200/80 p-5">
          <h4 className="text-sm font-semibold text-stone-800 mb-4">Duruma Göre Sipariş Dağılımı</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconSize={8}
                  formatter={(value: string) => <span className="text-xs text-stone-600">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Collection Chart */}
        <div className="bg-white rounded-xl border border-stone-200/80 p-5">
          <h4 className="text-sm font-semibold text-stone-800 mb-4">Tahsilat Grafiği</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={collectionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CurrencyTooltip />} />
                <Line type="monotone" dataKey="amount" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cargo Distribution */}
        <div className="bg-white rounded-xl border border-stone-200/80 p-5">
          <h4 className="text-sm font-semibold text-stone-800 mb-4">Kargo Durum Dağılımı</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={cargoDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {cargoDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconSize={8}
                  formatter={(value: string) => <span className="text-xs text-stone-600">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Report List */}
      <div>
        <h4 className="text-sm font-semibold text-stone-800 mb-3">Raporlar</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {reportList.map((report, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-stone-200/80 p-4 hover:border-stone-300 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-lg bg-gold-50 flex items-center justify-center mb-3 group-hover:bg-gold-100 transition-colors">
                <i className={`${report.icon} text-gold-500 text-lg`} />
              </div>
              <h5 className="text-sm font-semibold text-stone-800 mb-1">{report.title}</h5>
              <p className="text-xs text-stone-400">{report.desc}</p>
              <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                <span className="text-xs text-gold-500 font-medium">Görüntüle</span>
                <i className="ri-arrow-right-line text-gold-500 text-sm" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}